import { serverErrorMessage } from '@/services/httpService'
import { SealedFieldsForm } from '@/components/sealed/SealedFields'
import { OperatedEntity, useEntityKinds, useSaveEntityAttributes } from '@/services/coachBenefits'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * What members read before choosing this coach.
 *
 * Every field, and the whole form, comes from the `SealedTypeSchema` the server publishes for this
 * entity kind — see [SealedFieldsForm]. Nothing here names a headline or a bio.
 *
 * That is not tidiness. A save REPLACES the entity's whole attribute object, so a form built from a
 * hard-coded field list silently deletes anything it has not heard of: this page shipped knowing
 * four fields while the sealed type declared six, and editing a headline cleared the coach's
 * website and reset `acceptingStudents`. Rendering from the schema makes that class of bug
 * unreachable — the form always covers exactly what the type declares.
 */
export const CoachProfileEditor = ({ entity }: { entity: OperatedEntity }) => {
  const { t } = useTranslation()
  const save = useSaveEntityAttributes(entity.kind, entity.id)
  const { data: kinds, isLoading: schemaLoading } = useEntityKinds()
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const schema = kinds?.find((candidate) => candidate.kind === entity.kind)?.attributes ?? null

  /**
   * The value being edited, seeded from the server and carrying its discriminator.
   *
   * `kind` is set here because a new profile may have no attributes at all, and the schema selects
   * a variant by the discriminator — without it there is nothing to render a form from.
   */
  const [draft, setDraft] = useState<Record<string, unknown>>(() => ({
    ...(entity.attributes ?? {}),
    kind: (entity.attributes as Record<string, unknown> | null)?.kind ?? entity.kind,
  }))

  const onSave = () => {
    setError(null)
    setSaved(false)
    save.mutate(draft, {
      onSuccess: () => setSaved(true),
      onError: (e) => setError(serverErrorMessage(e, t('coach.saveProfileFailed'))),
    })
  }

  if (schemaLoading) return <p className="py-6 text-sm text-gray-400">{t('coach.loading')}</p>

  // No schema means the server did not describe this kind. Saying so beats rendering an empty form
  // that looks like a profile with nothing in it.
  if (!schema) {
    return <p className="py-6 text-sm text-gray-500">{t('coach.noProfileSchema')}</p>
  }

  return (
    <div className="max-w-xl">
      <SealedFieldsForm schema={schema} value={draft} onChange={setDraft} disabled={save.isLoading} />

      {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      {saved && !error && <p className="mt-3 text-xs text-green-700">{t('coach.profileSaved')}</p>}

      <button
        className="mt-4 rounded-full bg-[#3A79E0] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#2D5FB8] disabled:opacity-40"
        disabled={save.isLoading}
        onClick={onSave}
      >
        {t('coach.saveProfile')}
      </button>
    </div>
  )
}
